import React from "react";
import { ProductDetailsModal } from "../../../../interfaces/ProductObject";
import {
  DINE_IN_ITEM_STATUS,
  resolveProductLine,
  normalizeDineInItemStatus,
} from "../../../../services/helpers/DineInSummaryHelper";
import DineInProductThumb from "./DineInProductThumb";
import "./styles/DineInFeedItemCard.css";

interface DineInFeedItemCardProps {
  /** Undefined while fetchDineInProducts is still resolving this productId. */
  product?: ProductDetailsModal;
  quantity: number;
  /** Chosen variant, if any — drives the "Title · Full" label and price. */
  variantId?: string;
  addedByName: string;
  addedAt: string;
  /** True when the current logged-in user added this item — same
   *  isSender-style convention as Message.tsx: right-aligned, accent color.
   *  Own avatar is omitted (position + color already say it's you). */
  isMine: boolean;
  /**
   * Kitchen status for this line (ORDERED / ACCEPTED / REJECTED / SERVED).
   * Placed items show a small chip (waiting / preparing / served) beside the
   * price so the table can see where each dish is. REJECTED restyles the whole
   * card: a rejected item is excluded from the running bill, so leaving it
   * looking like every other row makes the total look wrong.
   */
  status?: string;
  /** The kitchen's reason for turning the item down, when it gave one. Only
   *  read on a rejected item. */
  statusNote?: string;
  /** Opens DineInProductDetailsModal for this item — the card only has room
   *  for a single truncated title line, so the full description/attributes
   *  live behind a tap instead. */
  onOpenDetails: () => void;
}

/**
 * One "added to the table" row: who (avatar, matches the reference
 * screenshot's chat-row feel) + what (real product thumbnail, title, line
 * price) + when. Price is the line total (unit price × quantity), same
 * convention as the header's running bill total in dineInFeedBuilder.ts.
 */
/** Chip per kitchen status. Draft items (and any unknown status) get none. */
const STATUS_CHIPS: Record<string, { label: string; modifier: string }> = {
  [DINE_IN_ITEM_STATUS.ORDERED]: { label: "Waiting for kitchen", modifier: "waiting" },
  [DINE_IN_ITEM_STATUS.ACCEPTED]: { label: "Preparing", modifier: "preparing" },
  [DINE_IN_ITEM_STATUS.SERVED]: { label: "✓ Served", modifier: "served" },
};

const DineInFeedItemCard: React.FC<DineInFeedItemCardProps> = ({
  product,
  quantity,
  variantId,
  addedByName,
  addedAt,
  isMine,
  status,
  statusNote,
  onOpenDetails,
}) => {
  // Same resolver the bill totals use, so the row and the total can't disagree.
  const resolved = resolveProductLine(product, variantId);
  const title = resolved.displayTitle || "Menu item";
  const lineTotal = resolved.unitPrice != null ? resolved.unitPrice * quantity : null;
  const normalizedStatus = normalizeDineInItemStatus(status);
  const isRejected = normalizedStatus === DINE_IN_ITEM_STATUS.REJECTED;
  const chip = STATUS_CHIPS[normalizedStatus];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpenDetails();
    }
  };

  return (
    <div className={`dine-in-feed-item-row${isMine ? " dine-in-feed-item-row--mine" : ""}`}>
      {!isMine && (
        <span className="dine-in-feed-item-row__avatar">
          {addedByName.slice(0, 2).toUpperCase()}
        </span>
      )}
      <div
        className={`dine-in-feed-item-card${isMine ? " dine-in-feed-item-card--mine" : ""}${
          isRejected ? " dine-in-feed-item-card--rejected" : ""
        }`}
        role="button"
        tabIndex={0}
        onClick={onOpenDetails}
        onKeyDown={handleKeyDown}
        aria-label={`View details for ${title}`}
      >
        <DineInProductThumb product={product} />
        <div className="dine-in-feed-item-card__info">
          <span className="dine-in-feed-item-card__title">
            <span className="dine-in-feed-item-card__name">{title}</span>
            {quantity > 1 && (
              <span className="dine-in-feed-item-card__qty"> ×{quantity}</span>
            )}
          </span>
          {isRejected ? (
            // The price is replaced rather than struck through: the number
            // isn't in the total, so showing it at all invites adding it up.
            // The kitchen's own words go here when it gave a reason — "Out of
            // stock · not charged" answers the question before it's asked,
            // where a generic "Unavailable" just prompts someone to go and
            // ask. Falls back only when no reason was recorded.
            <span className="dine-in-feed-item-card__rejected-note">
              {statusNote?.trim() || "Unavailable"} · not charged
            </span>
          ) : (
            (lineTotal != null || chip) && (
              <span className="dine-in-feed-item-card__price-row">
                {lineTotal != null && (
                  <span className="dine-in-feed-item-card__price">₹{Math.round(lineTotal)}</span>
                )}
                {chip && (
                  <span
                    className={`dine-in-feed-item-card__status dine-in-feed-item-card__status--${chip.modifier}`}
                  >
                    {chip.label}
                  </span>
                )}
              </span>
            )
          )}
          <span className="dine-in-feed-item-card__byline">
            Added by {addedByName} &middot; {addedAt}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DineInFeedItemCard;
